import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import {
  STREAMLAIN_DISCLOSURE_ENTER_TIMING,
  streamLainDisclosureOverlayKeyframesCss,
  streamLainDisclosurePopoverShellStyles,
} from '@blocksuite/affine-shared/utils';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  ArrowLeftBigIcon,
  ArrowLeftSmallIcon,
  CloseIcon,
  SearchIcon,
} from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { RANGE_SYNC_EXCLUDE_ATTR } from '@blocksuite/std/inline';
import {
  autoPlacement,
  autoUpdate,
  computePosition,
  flip,
  type Middleware,
  offset,
  type Placement,
  type ReferenceElement,
  shift,
  size,
} from '@floating-ui/dom';
import { css, html, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { MenuFocusable } from './focusable.js';
import {
  isStreamLainDisclosureMenu,
  isStreamLainDisclosureMenuClassName,
  Menu,
  type MenuConfig,
  type MenuOptions,
} from './menu.js';
import type { MenuComponentInterface } from './types.js';

const DISCLOSURE_MENU_SIDE_ATTR = 'data-streamlain-disclosure-menu-side';
const DISCLOSURE_MENU_TAIL_CLASS =
  'streamlain-edgeless-toolbar-disclosure-tail';
const DISCLOSURE_MENU_TAIL_FILL_CLASS =
  'streamlain-edgeless-toolbar-disclosure-tail-fill';
const DISCLOSURE_MENU_TAIL_BORDER_CLASS =
  'streamlain-edgeless-toolbar-disclosure-tail-border';
const DISCLOSURE_MENU_TAIL_LEFT_VAR = '--streamlain-disclosure-menu-tail-left';
const DISCLOSURE_MENU_TAIL_TOP_VAR = '--streamlain-disclosure-menu-tail-top';
const DISCLOSURE_MENU_ENTER_KEYFRAME =
  'streamlain-edgeless-toolbar-disclosure-enter';
const DISCLOSURE_MENU_ENTER_RIGHT_KEYFRAME =
  'streamlain-edgeless-toolbar-disclosure-enter-right';
const DISCLOSURE_MENU_ENTER_LEFT_KEYFRAME =
  'streamlain-edgeless-toolbar-disclosure-enter-left';
const DISCLOSURE_MENU_TAIL_HALF_WIDTH = 15;
const DISCLOSURE_MENU_TAIL_WIDTH = DISCLOSURE_MENU_TAIL_HALF_WIDTH * 2;
const DISCLOSURE_MENU_TAIL_STROKE_WIDTH = 0.5;
const DISCLOSURE_MENU_TAIL_HEIGHT = 15;
const DISCLOSURE_MENU_TAIL_PANEL_OVERLAP = 0.25;
const DISCLOSURE_MENU_TAIL_CORNER_CLEARANCE = 8;

export class MenuComponent
  extends SignalWatcher(WithDisposable(ShadowlessElement))
  implements MenuComponentInterface
{
  static override styles = css`
    ${unsafeCSS(streamLainDisclosurePopoverShellStyles)}
    ${unsafeCSS(
      streamLainDisclosureOverlayKeyframesCss({
        enter: DISCLOSURE_MENU_ENTER_KEYFRAME,
        enterLeft: DISCLOSURE_MENU_ENTER_LEFT_KEYFRAME,
        enterRight: DISCLOSURE_MENU_ENTER_RIGHT_KEYFRAME,
      })
    )}

    affine-menu {
      font-family: var(--streamlain-font-family);
      display: flex;
      flex-direction: column;
      user-select: none;
      min-width: 320px;
      max-width: 320px;
      max-height: 700px;
      box-shadow: ${unsafeCSSVarV2('shadow/overlayPanel')};
      border-radius: 4px;
      background-color: ${unsafeCSSVarV2('layer/background/overlayPanel')};
      padding: 8px;
      position: absolute;
      z-index: 999;
      gap: 8px;
      border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
      color: ${unsafeCSSVarV2('text/primary')};
      overflow: hidden;
      min-height: 0;
      overscroll-behavior: contain;
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu {
      --streamlain-disclosure-overlay-width: 260px;
      width: auto;
      min-width: 0;
      max-width: none;
      max-height: none;
      min-height: 0;
      padding: 0;
      gap: 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
      overflow: visible;
      color: var(--streamlain-v2-text-primary);
    }

    .streamlain-edgeless-toolbar-disclosure-shell {
      width: min(
        var(--streamlain-disclosure-overlay-width, 260px),
        calc(100vw - 32px)
      );
      min-width: min(220px, calc(100vw - 32px));
      max-width: min(320px, calc(100vw - 32px));
      position: relative;
      overflow: visible;
      transform-origin: var(${unsafeCSS(DISCLOSURE_MENU_TAIL_LEFT_VAR)}, 50%)
        calc(100% + 10px);
      will-change: opacity, transform;
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu:not(
        [data-streamlain-disclosure-menu-ready='true']
      )
      .streamlain-edgeless-toolbar-disclosure-shell {
      opacity: 0;
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu[data-streamlain-disclosure-menu-ready='true']
      .streamlain-edgeless-toolbar-disclosure-shell {
      animation: ${unsafeCSS(DISCLOSURE_MENU_ENTER_KEYFRAME)}
        ${unsafeCSS(STREAMLAIN_DISCLOSURE_ENTER_TIMING)};
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu[${unsafeCSS(
        DISCLOSURE_MENU_SIDE_ATTR
      )}='bottom']
      .streamlain-edgeless-toolbar-disclosure-shell {
      transform-origin: var(
          ${unsafeCSS(DISCLOSURE_MENU_TAIL_LEFT_VAR)},
          50%
        ) -10px;
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu[${unsafeCSS(
        DISCLOSURE_MENU_SIDE_ATTR
      )}='right']
      .streamlain-edgeless-toolbar-disclosure-shell {
      transform-origin: -10px
        var(${unsafeCSS(DISCLOSURE_MENU_TAIL_TOP_VAR)}, 50%);
      animation-name: ${unsafeCSS(DISCLOSURE_MENU_ENTER_RIGHT_KEYFRAME)};
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu[${unsafeCSS(
        DISCLOSURE_MENU_SIDE_ATTR
      )}='left']
      .streamlain-edgeless-toolbar-disclosure-shell {
      transform-origin: calc(100% + 10px)
        var(${unsafeCSS(DISCLOSURE_MENU_TAIL_TOP_VAR)}, 50%);
      animation-name: ${unsafeCSS(DISCLOSURE_MENU_ENTER_LEFT_KEYFRAME)};
    }

    .streamlain-edgeless-toolbar-disclosure-tail {
      position: absolute;
      left: var(${unsafeCSS(DISCLOSURE_MENU_TAIL_LEFT_VAR)}, 50%);
      top: calc(
        100% - ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_PANEL_OVERLAP))}px
      );
      width: ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_WIDTH))}px;
      height: ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_HEIGHT))}px;
      transform: translateX(-50%);
      overflow: visible;
      pointer-events: none;
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu[${unsafeCSS(
        DISCLOSURE_MENU_SIDE_ATTR
      )}='bottom']
      .streamlain-edgeless-toolbar-disclosure-tail {
      top: auto;
      bottom: calc(
        100% + ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_PANEL_OVERLAP))}px
      );
      transform: translateX(-50%) scaleY(-1);
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu[${unsafeCSS(
        DISCLOSURE_MENU_SIDE_ATTR
      )}='right']
      .streamlain-edgeless-toolbar-disclosure-tail {
      left: 0;
      right: auto;
      top: var(${unsafeCSS(DISCLOSURE_MENU_TAIL_TOP_VAR)}, 50%);
      bottom: auto;
      width: ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_HEIGHT))}px;
      height: ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_WIDTH))}px;
      transform: translate(
        calc(
          -100% + ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_PANEL_OVERLAP))}px
        ),
        -50%
      );
    }

    affine-menu.streamlain-edgeless-toolbar-disclosure-menu[${unsafeCSS(
        DISCLOSURE_MENU_SIDE_ATTR
      )}='left']
      .streamlain-edgeless-toolbar-disclosure-tail {
      left: 100%;
      right: auto;
      top: var(${unsafeCSS(DISCLOSURE_MENU_TAIL_TOP_VAR)}, 50%);
      bottom: auto;
      width: ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_HEIGHT))}px;
      height: ${unsafeCSS(String(DISCLOSURE_MENU_TAIL_WIDTH))}px;
      transform: translate(
        -${unsafeCSS(String(DISCLOSURE_MENU_TAIL_PANEL_OVERLAP))}px,
        -50%
      );
    }

    .streamlain-edgeless-toolbar-disclosure-tail svg {
      display: block;
      width: 100%;
      height: 100%;
      overflow: visible;
    }

    .streamlain-edgeless-toolbar-disclosure-tail-fill {
      fill: var(
        --streamlain-v2-layer-background-overlayPanel,
        var(--streamlain-v2-layer-background-primary)
      );
      stroke: none;
    }

    .streamlain-edgeless-toolbar-disclosure-tail-border {
      fill: none;
      stroke: var(--streamlain-v2-layer-insideBorder-border);
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 0.5px;
      vector-effect: non-scaling-stroke;
    }

    @media (prefers-reduced-motion: reduce) {
      affine-menu.streamlain-edgeless-toolbar-disclosure-menu[data-streamlain-disclosure-menu-ready='true']
        .streamlain-edgeless-toolbar-disclosure-shell {
        animation: none;
      }
    }

    .streamlain-edgeless-toolbar-disclosure-scroll {
      max-height: min(
        var(--streamlain-disclosure-popover-scroll-max-height, 360px),
        calc(100vh - 112px)
      );
    }

    .streamlain-edgeless-toolbar-disclosure-body {
      padding: 8px;
    }

    .streamlain-edgeless-toolbar-disclosure-menu-body {
      gap: 2px;
    }

    .affine-menu-search-container {
      border-radius: 4px;
      display: flex;
      align-items: center;
      padding: 4px 10px;
      gap: 8px;
      border: 1px solid ${unsafeCSSVarV2('input/border/default')};
    }

    .affine-menu-search {
      flex: 1;
      outline: none;
      font-size: 14px;
      line-height: 22px;
      border: none;
      background-color: transparent;
    }

    .affine-menu-body {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    affine-menu.data-view-nested-options-menu > .affine-menu-body {
      box-sizing: border-box;
      padding-inline: 8px;
    }

    affine-menu.data-view-nested-options-menu
      > .affine-menu-body
      > affine-menu-button {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    .no-results {
      font-size: 12px;
      line-height: 20px;
      color: var(--streamlain-v2-text-secondary);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 8px;
    }
  `;

  private readonly _clickContainer = (e: MouseEvent) => {
    e.stopPropagation();
    this.focusInput();
    this.menu.closeSubMenu();
  };

  private readonly _stopScrollPropagation = (e: Event) => {
    e.stopPropagation();
  };

  private readonly searchRef = createRef<HTMLInputElement>();

  override connectedCallback() {
    super.connectedCallback();
    this.role = 'menu';

    this.disposables.addFromEvent(this, 'wheel', this._stopScrollPropagation, {
      passive: true,
    });
    this.disposables.addFromEvent(
      this,
      'touchmove',
      this._stopScrollPropagation,
      {
        passive: true,
      }
    );
  }

  override firstUpdated() {
    const input = this.searchRef.value;
    if (input) {
      requestAnimationFrame(() => {
        this.focusInput();
      });
      const length = input.value.length;
      input.setSelectionRange(length, length);
      this.disposables.addFromEvent(input, 'keydown', e => {
        e.stopPropagation();
        if (e.key === 'Escape') {
          this.menu.close();
          return;
        }
        const onBack = this.menu.options.title?.onBack;
        if (e.key === 'Backspace' && onBack && !this.menu.showSearch$.value) {
          this.menu.close();
          onBack(this.menu);
          return;
        }
        if (e.key === 'Enter' && !e.isComposing) {
          this.menu.pressEnter();
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.menu.focusPrev();
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.menu.focusNext();
          return;
        }
      });

      this.disposables.addFromEvent(input, 'copy', e => {
        e.stopPropagation();
      });
      this.disposables.addFromEvent(input, 'cut', e => {
        e.stopPropagation();
      });
      this.disposables.addFromEvent(this, 'click', this._clickContainer);
    }
  }

  focusInput() {
    const showSearch =
      this.menu.showSearch$.value || this.menu.options.search?.placeholder;
    if (showSearch) {
      this.searchRef.value?.focus();
    }
  }

  focusTo(ele?: MenuFocusable) {
    this.menu.setFocusOnly(ele);
    this.focusInput();
  }

  getFirstFocusableElement(): HTMLElement | null {
    return this.querySelector('[data-focusable="true"]');
  }

  getFocusableElements(): HTMLElement[] {
    return Array.from(this.querySelectorAll('[data-focusable="true"]'));
  }

  override render() {
    const result = this.menu.renderItems(this.menu.options.items);
    if (isStreamLainDisclosureMenu(this.menu)) {
      return html`
        <div
          class="streamlain-disclosure-popover-shell streamlain-edgeless-toolbar-disclosure-shell"
        >
          <div class=${DISCLOSURE_MENU_TAIL_CLASS} aria-hidden="true">
            <svg focusable="false">
              <path class=${DISCLOSURE_MENU_TAIL_FILL_CLASS}></path>
              <path class=${DISCLOSURE_MENU_TAIL_BORDER_CLASS}></path>
            </svg>
          </div>
          <div
            class="streamlain-disclosure-popover-scroll streamlain-edgeless-toolbar-disclosure-scroll"
          >
            <div
              class="streamlain-disclosure-popover-body streamlain-edgeless-toolbar-disclosure-body"
            >
              ${this.renderTitle()} ${this.renderSearch()}
              <div
                class="affine-menu-body streamlain-edgeless-toolbar-disclosure-menu-body"
              >
                ${result.length === 0 && this.menu.enableSearch
                  ? html` <div class="no-results">No Results</div>`
                  : ''}
                ${result}
              </div>
            </div>
          </div>
        </div>
      `;
    }
    return html`
      ${this.renderTitle()} ${this.renderSearch()}
      <div class="affine-menu-body">
        ${result.length === 0 && this.menu.enableSearch
          ? html` <div class="no-results">No Results</div>`
          : ''}
        ${result}
      </div>
    `;
  }

  renderSearch() {
    const config = this.menu.options.search;
    const showSearch = this.menu.showSearch$.value || config?.placeholder;
    const searchStyle = styleMap({
      display: showSearch ? undefined : 'none',
      opacity: showSearch ? '1' : '0',
      height: showSearch ? undefined : '0',
      overflow: showSearch ? undefined : 'hidden',
      position: showSearch ? undefined : 'absolute',
      pointerEvents: showSearch ? undefined : 'none',
    });
    return html` <div style=${searchStyle} class="affine-menu-search-container">
      <div
        style="font-size:20px;display:flex;align-items:center;color: var(--streamlain-v2-text-secondary)"
      >
        ${SearchIcon()}
      </div>
      <input
        autocomplete="off"
        class="affine-menu-search"
        placeholder="${config?.placeholder ?? ''}"
        data-1p-ignore
        ${ref(this.searchRef)}
        type="text"
        value="${this.menu.searchName$.value}"
        @input="${(e: Event) =>
          (this.menu.searchName$.value = (e.target as HTMLInputElement).value)}"
      />
    </div>`;
  }

  renderTitle() {
    const title = this.menu.options.title;
    if (!title) {
      return;
    }
    return html`
      <div
        style="display:flex;align-items:center;gap: 4px;padding:3px 8px"
        @mouseenter="${() => this.menu.closeSubMenu()}"
      >
        ${title.onBack
          ? html` <div
              @click="${() => {
                title.onBack?.(this.menu);
                this.menu.close();
              }}"
              class="dv-icon-20 dv-hover dv-pd-2 dv-round-4"
              style="display:flex;"
            >
              ${ArrowLeftBigIcon()}
            </div>`
          : nothing}
        <div
          style="flex:1;font-weight:500;font-size: 14px;line-height: 22px;color: var(--streamlain-v2-text-primary)"
        >
          ${title.text}
        </div>
        ${title.postfix?.()}
        ${title.onClose
          ? html` <div
              @click="${() => {
                title.onClose?.();
                this.menu.close();
              }}"
              class="dv-icon-20 dv-hover dv-pd-2 dv-round-4"
              style="display:flex;"
            >
              ${CloseIcon()}
            </div>`
          : nothing}
      </div>
    `;
  }

  @property({ attribute: false })
  accessor menu!: Menu;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-menu': MenuComponent;
  }
}

export class MobileMenuComponent
  extends SignalWatcher(WithDisposable(ShadowlessElement))
  implements MenuComponentInterface
{
  static override styles = css`
    mobile-menu {
      height: 100%;
      font-family: var(--streamlain-font-family);
      display: flex;
      flex-direction: column;
      user-select: none;
      width: 100%;
      background-color: ${unsafeCSSVarV2('layer/background/secondary')};
      padding: calc(8px + env(safe-area-inset-top, 0px)) 8px
        calc(8px + env(safe-area-inset-bottom, 0px)) 8px;
      position: absolute;
      z-index: 999;
      color: ${unsafeCSSVarV2('text/primary')};
    }

    .mobile-menu-body {
      display: flex;
      flex-direction: column;
      padding: 24px 16px;
      gap: 16px;
      flex: 1;
      overflow-y: auto;
    }
  `;

  onClose = () => {
    const close = this.menu.options.title?.onClose;
    if (close) {
      close();
    } else {
      this.menu.close();
    }
  };

  focusTo(ele?: MenuFocusable) {
    this.menu.setFocusOnly(ele);
  }

  getFirstFocusableElement(): HTMLElement | null {
    return this.querySelector('[data-focusable="true"]');
  }

  getFocusableElements(): HTMLElement[] {
    return Array.from(this.querySelectorAll('[data-focusable="true"]'));
  }

  override render() {
    const result = this.menu.renderItems(this.menu.options.items);
    return html`
      ${this.renderTitle()}
      <div class="mobile-menu-body">${result}</div>
    `;
  }

  renderTitle() {
    const title = this.menu.options.title;
    return html`
      <div
        style="display:flex;align-items:center;height: 44px;"
        @mouseenter="${() => this.menu.closeSubMenu()}"
      >
        <div style="width: 50px;flex-shrink: 0;margin-left: 10px;">
          ${title?.onBack
            ? html` <div
                @click="${() => {
                  title.onBack?.(this.menu);
                  this.menu.close();
                }}"
                style="
                display:flex;
                font-size: 24px;
                align-items:center;
"
              >
                ${ArrowLeftSmallIcon()}
              </div>`
            : nothing}
        </div>
        <div
          style="
          flex:1;
          font-size: 17px;
          font-style: normal;
          font-weight: 500;
          line-height: 22px;
          color: var(--streamlain-v2-text-primary);
          display: flex;
          justify-content: center;
"
        >
          ${title?.text}
        </div>
        <div
          @click="${this.onClose}"
          style="
          display:flex;
          font-weight: 500;
          font-size: 17px;
          color: ${unsafeCSSVarV2('button/primary')};
          width: 50px;
          flex-shrink: 0;
          margin-right: 10px;
         "
        >
          Done
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor menu!: Menu;
}

declare global {
  interface HTMLElementTagNameMap {
    'mobile-menu': MobileMenuComponent;
  }
}

export const getDefaultModalRoot = (ele: HTMLElement) => {
  const host: HTMLElement | null =
    ele.closest('editor-host') ?? ele.closest('.data-view-popup-container');
  if (host) {
    return host;
  }
  return document.body;
};
export const createModal = (container: HTMLElement = document.body) => {
  const div = document.createElement('div');
  div.setAttribute(RANGE_SYNC_EXCLUDE_ATTR, 'true');
  div.style.pointerEvents = 'auto';
  div.style.position = 'absolute';
  div.style.left = '0';
  div.style.top = '0';
  div.style.width = '100%';
  div.style.height = '100%';
  div.style.zIndex = '1001';
  div.style.fontFamily = 'var(--streamlain-font-family)';
  container.append(div);
  return div;
};
export type PopupTarget = {
  targetRect: ReferenceElement;
  root: HTMLElement;
  popupStart: () => () => void;
  popupLifetime?: PopupLifetime;
};

export type PopupLifetime = {
  register: (close: () => void) => () => void;
};

const popupLifetimeByMenu = new WeakMap<HTMLElement, PopupLifetime>();

export const popupTargetFromElement = (element: HTMLElement): PopupTarget => {
  let rect = element.getBoundingClientRect();
  let count = 0;
  let isActive = false;
  return {
    targetRect: {
      getBoundingClientRect: () => {
        if (element.isConnected) {
          return (rect = element.getBoundingClientRect());
        }
        return rect;
      },
    },
    root: getDefaultModalRoot(element),
    popupLifetime: popupLifetimeByMenu.get(
      element.closest<HTMLElement>('affine-menu') ?? element
    ),
    popupStart: () => {
      if (!count) {
        isActive = element.classList.contains('active');
        if (!isActive) {
          element.classList.add('active');
        }
      }
      count++;
      return () => {
        count--;
        if (!count && !isActive) {
          element.classList.remove('active');
        }
      };
    },
  };
};

type DisclosureMenuSide = 'top' | 'bottom' | 'left' | 'right';
type DisclosureMenuTailEdge = 'top' | 'right' | 'bottom' | 'left';
type RectLike = Pick<DOMRect, 'height' | 'left' | 'top' | 'width'>;

function getDisclosureMenuPanelLayoutRect(
  menuElement: HTMLElement,
  shell: HTMLElement
): RectLike {
  const menuRect = menuElement.getBoundingClientRect();
  return {
    height: shell.offsetHeight,
    left: menuRect.left + shell.offsetLeft,
    top: menuRect.top + shell.offsetTop,
    width: shell.offsetWidth,
  };
}

function getDisclosureMenuSide(
  menuRect: RectLike,
  triggerRect: RectLike,
  placement?: Placement
): DisclosureMenuSide {
  const placementSide = placement?.split('-')[0];
  if (
    placementSide === 'top' ||
    placementSide === 'bottom' ||
    placementSide === 'left' ||
    placementSide === 'right'
  ) {
    return placementSide;
  }

  const menuCenterX = menuRect.left + menuRect.width / 2;
  const menuCenterY = menuRect.top + menuRect.height / 2;
  const triggerCenterX = triggerRect.left + triggerRect.width / 2;
  const triggerCenterY = triggerRect.top + triggerRect.height / 2;
  const deltaX = menuCenterX - triggerCenterX;
  const deltaY = menuCenterY - triggerCenterY;

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX > 0 ? 'right' : 'left';
  }
  return deltaY > 0 ? 'bottom' : 'top';
}

export function syncStreamLainDisclosureMenuChrome(
  menuElement: HTMLElement,
  trigger: ReferenceElement,
  placement?: Placement,
  attempt = 0
) {
  if (
    !isStreamLainDisclosureMenuClassName(menuElement.className) ||
    menuElement.dataset.streamlainDisclosureMenuClosing === 'true'
  ) {
    return;
  }

  const shell = menuElement.querySelector<HTMLElement>(
    '.streamlain-edgeless-toolbar-disclosure-shell'
  );
  if (!shell || shell.offsetWidth <= 0 || shell.offsetHeight <= 0) {
    if (attempt < 8) {
      requestAnimationFrame(() =>
        syncStreamLainDisclosureMenuChrome(
          menuElement,
          trigger,
          placement,
          attempt + 1
        )
      );
    }
    return;
  }

  const triggerRect = trigger.getBoundingClientRect();
  const panelRect = getDisclosureMenuPanelLayoutRect(menuElement, shell);
  const side = getDisclosureMenuSide(panelRect, triggerRect, placement);
  menuElement.setAttribute(DISCLOSURE_MENU_SIDE_ATTR, side);

  if (side === 'left' || side === 'right') {
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const minTailInset = getStreamLainDisclosureMenuTailMinInset(
      shell,
      side === 'right' ? 'left' : 'right'
    );
    const tailTop = clampStreamLainDisclosureMenuTailOffset(
      triggerCenterY - panelRect.top,
      panelRect.height,
      minTailInset
    );
    shell.style.setProperty(
      DISCLOSURE_MENU_TAIL_TOP_VAR,
      `${Math.round(tailTop * 100) / 100}px`
    );
    updateStreamLainDisclosureMenuSideTailPath(shell, side);
  } else {
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const minTailInset = getStreamLainDisclosureMenuTailMinInset(
      shell,
      side === 'bottom' ? 'top' : 'bottom'
    );
    const tailLeft = clampStreamLainDisclosureMenuTailOffset(
      triggerCenterX - panelRect.left,
      panelRect.width,
      minTailInset
    );
    shell.style.setProperty(
      DISCLOSURE_MENU_TAIL_LEFT_VAR,
      `${Math.round(tailLeft * 100) / 100}px`
    );
    updateStreamLainDisclosureMenuVerticalTailPath(shell);
  }

  menuElement.dataset.streamlainDisclosureMenuReady = 'true';
}

function clampStreamLainDisclosureMenuTailOffset(
  offset: number,
  panelLength: number,
  minInset: number
) {
  if (panelLength <= minInset * 2) {
    return panelLength / 2;
  }

  return Math.min(Math.max(offset, minInset), panelLength - minInset);
}

function getStreamLainDisclosureMenuTailMinInset(
  shell: HTMLElement,
  edge: DisclosureMenuTailEdge
) {
  return (
    DISCLOSURE_MENU_TAIL_HALF_WIDTH +
    DISCLOSURE_MENU_TAIL_STROKE_WIDTH / 2 +
    getStreamLainDisclosureMenuTailCornerClearance(shell, edge)
  );
}

function getStreamLainDisclosureMenuTailCornerClearance(
  shell: HTMLElement,
  edge: DisclosureMenuTailEdge
) {
  const styles = getComputedStyle(shell);
  const radii =
    edge === 'top'
      ? [styles.borderTopLeftRadius, styles.borderTopRightRadius]
      : edge === 'right'
        ? [styles.borderTopRightRadius, styles.borderBottomRightRadius]
        : edge === 'bottom'
          ? [styles.borderBottomLeftRadius, styles.borderBottomRightRadius]
          : [styles.borderTopLeftRadius, styles.borderBottomLeftRadius];

  return Math.max(
    DISCLOSURE_MENU_TAIL_CORNER_CLEARANCE,
    ...radii.map(parseStreamLainDisclosureMenuCssPixelValue)
  );
}

function parseStreamLainDisclosureMenuCssPixelValue(value: string) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

function getStreamLainDisclosureMenuTailPaths(shell: HTMLElement) {
  const tail = shell.querySelector<HTMLElement>(
    `.${DISCLOSURE_MENU_TAIL_CLASS}`
  );
  return {
    borderPath: tail?.querySelector<SVGPathElement>(
      `.${DISCLOSURE_MENU_TAIL_BORDER_CLASS}`
    ),
    fillPath: tail?.querySelector<SVGPathElement>(
      `.${DISCLOSURE_MENU_TAIL_FILL_CLASS}`
    ),
    svg: tail?.querySelector<SVGSVGElement>('svg'),
  };
}

function updateStreamLainDisclosureMenuVerticalTailPath(shell: HTMLElement) {
  const { borderPath, fillPath, svg } =
    getStreamLainDisclosureMenuTailPaths(shell);
  if (!borderPath || !fillPath || !svg) return;

  const tailHeight = DISCLOSURE_MENU_TAIL_HEIGHT;
  const strokeOffset = DISCLOSURE_MENU_TAIL_STROKE_WIDTH / 2;
  const strokeY = strokeOffset;
  const tipY = tailHeight - strokeOffset;
  const left = strokeOffset;
  const right = DISCLOSURE_MENU_TAIL_WIDTH - strokeOffset;
  const center = DISCLOSURE_MENU_TAIL_HALF_WIDTH;
  const entryCurveX = 5.6;
  const bendCurveX = 8.4;
  const curveY = 5.2;
  const tipRadius = 1.45;
  const leftTipX = center - tipRadius;
  const rightTipX = center + tipRadius;
  const tipShoulderY = tipY - 1.1;

  svg.setAttribute(
    'viewBox',
    `0 0 ${DISCLOSURE_MENU_TAIL_WIDTH} ${tailHeight}`
  );
  fillPath.setAttribute(
    'd',
    `M ${left} 0 C ${left + entryCurveX} 0 ${left + bendCurveX} ${curveY} ${leftTipX} ${tipShoulderY} Q ${center} ${tailHeight} ${rightTipX} ${tipShoulderY} C ${right - bendCurveX} ${curveY} ${right - entryCurveX} 0 ${right} 0 Z`
  );
  borderPath.setAttribute(
    'd',
    `M ${left} ${strokeY} C ${left + entryCurveX} ${strokeY} ${left + bendCurveX} ${curveY} ${leftTipX} ${tipShoulderY} Q ${center} ${tipY} ${rightTipX} ${tipShoulderY} C ${right - bendCurveX} ${curveY} ${right - entryCurveX} ${strokeY} ${right} ${strokeY}`
  );
}

function updateStreamLainDisclosureMenuSideTailPath(
  shell: HTMLElement,
  side: 'left' | 'right'
) {
  const { borderPath, fillPath, svg } =
    getStreamLainDisclosureMenuTailPaths(shell);
  if (!borderPath || !fillPath || !svg) return;

  const tailWidth = DISCLOSURE_MENU_TAIL_HEIGHT;
  const tailHeight = DISCLOSURE_MENU_TAIL_WIDTH;
  const strokeOffset = DISCLOSURE_MENU_TAIL_STROKE_WIDTH / 2;
  const top = strokeOffset;
  const bottom = tailHeight - strokeOffset;
  const center = DISCLOSURE_MENU_TAIL_HALF_WIDTH;
  const entryCurveY = 5.6;
  const bendCurveY = 8.4;
  const curveInsetX = 5.2;
  const tipRadius = 1.45;
  const upperTipY = center - tipRadius;
  const lowerTipY = center + tipRadius;
  const tipShoulderInsetX = 1.35;

  svg.setAttribute('viewBox', `0 0 ${tailWidth} ${tailHeight}`);

  if (side === 'right') {
    const panelX = tailWidth - strokeOffset;
    const fillPanelX = tailWidth;
    const tipX = strokeOffset;
    const shoulderX = tipShoulderInsetX;
    const curveX = tailWidth - curveInsetX;

    fillPath.setAttribute(
      'd',
      `M ${fillPanelX} 0 C ${fillPanelX} ${entryCurveY} ${curveX} ${bendCurveY} ${shoulderX} ${upperTipY} Q 0 ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${tailHeight - bendCurveY} ${fillPanelX} ${tailHeight - entryCurveY} ${fillPanelX} ${tailHeight} Z`
    );
    borderPath.setAttribute(
      'd',
      `M ${panelX} ${top} C ${panelX} ${top + entryCurveY} ${curveX} ${top + bendCurveY} ${shoulderX} ${upperTipY} Q ${tipX} ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${bottom - bendCurveY} ${panelX} ${bottom - entryCurveY} ${panelX} ${bottom}`
    );
    return;
  }

  const panelX = strokeOffset;
  const fillPanelX = 0;
  const tipX = tailWidth - strokeOffset;
  const shoulderX = tailWidth - tipShoulderInsetX;
  const curveX = curveInsetX;

  fillPath.setAttribute(
    'd',
    `M ${fillPanelX} 0 C ${fillPanelX} ${entryCurveY} ${curveX} ${bendCurveY} ${shoulderX} ${upperTipY} Q ${tailWidth} ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${tailHeight - bendCurveY} ${fillPanelX} ${tailHeight - entryCurveY} ${fillPanelX} ${tailHeight} Z`
  );
  borderPath.setAttribute(
    'd',
    `M ${panelX} ${top} C ${panelX} ${top + entryCurveY} ${curveX} ${top + bendCurveY} ${shoulderX} ${upperTipY} Q ${tipX} ${center} ${shoulderX} ${lowerTipY} C ${curveX} ${bottom - bendCurveY} ${panelX} ${bottom - entryCurveY} ${panelX} ${bottom}`
  );
}

export const createPopup = (
  target: PopupTarget,
  content: HTMLElement,
  options?: {
    onClose?: () => void;
    middleware?: Array<Middleware | null | undefined | false>;
    container?: HTMLElement;
    placement?: Placement;
  }
) => {
  const close = () => {
    modal.remove();
    options?.onClose?.();
  };
  const modal = createModal(target.root);
  const constrainToViewport = size({
    padding: 8,
    apply({ availableHeight }) {
      const height = `${Math.max(120, Math.floor(availableHeight))}px`;
      if (isStreamLainDisclosureMenuClassName(content.className)) {
        content.style.setProperty(
          '--streamlain-disclosure-popover-scroll-max-height',
          height
        );
        return;
      }
      if (!content.dataset.affineRequestedMinHeight) {
        content.dataset.affineRequestedMinHeight = content.style.minHeight;
      }
      content.style.maxHeight = height;
      const requestedMinHeight = content.dataset.affineRequestedMinHeight;
      if (requestedMinHeight && requestedMinHeight !== 'fit-content') {
        content.style.minHeight = `min(${requestedMinHeight}, ${height})`;
      }
    },
  });
  autoUpdate(target.targetRect, content, () => {
    computePosition(target.targetRect, content, {
      placement: options?.placement,
      middleware: [
        ...(options?.middleware ?? [shift({ crossAxis: true })]),
        constrainToViewport,
      ],
    })
      .then(({ placement, x, y }) => {
        Object.assign(content.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
        syncStreamLainDisclosureMenuChrome(
          content,
          target.targetRect,
          placement
        );
      })
      .catch(console.error);
  });
  modal.append(content);

  modal.onpointerdown = ev => {
    if (ev.target === modal) {
      close();
    }
  };

  modal.onmousedown = ev => {
    if (ev.target === modal) {
      close();
    }
  };

  modal.oncontextmenu = ev => {
    ev.preventDefault();
    if (ev.target === modal) {
      close();
    }
  };

  return close;
};

export type MenuHandler = {
  close: () => void;
  menu: Menu;
  reopen: () => void;
};

export const popMenu = (
  target: PopupTarget,
  props: {
    options: MenuOptions;
    middleware?: Array<Middleware | null | undefined | false>;
    container?: HTMLElement;
    placement?: Placement;
  }
): MenuHandler => {
  const popupEnd = target.popupStart();
  let unregisterLifetime = () => {};
  const onClose = () => {
    unregisterLifetime();
    props.options.onClose?.();
    popupEnd();
    closePopup();
  };
  const menu = new Menu({
    ...props.options,
    onClose: onClose,
  });
  const middleware =
    props.middleware ??
    (props.placement
      ? [offset(8), flip(), shift({ crossAxis: true })]
      : [
          autoPlacement({
            allowedPlacements: [
              'bottom-start',
              'bottom-end',
              'top-start',
              'top-end',
            ],
          }),
          offset(4),
        ]);
  const closePopup = createPopup(target, menu.menuElement, {
    onClose: () => {
      menu.close();
    },
    middleware,
    container: props.container,
    placement: props.placement,
  });
  const handler = {
    close: closePopup,
    menu,
    reopen: () => {
      popMenu(target, props);
    },
  };
  if (target.popupLifetime) {
    popupLifetimeByMenu.set(menu.menuElement, target.popupLifetime);
    unregisterLifetime = target.popupLifetime.register(handler.close);
  }
  return handler;
};
export const popFilterableSimpleMenu = (
  target: PopupTarget,
  options: MenuConfig[],
  onClose?: () => void,
  placement: Placement = 'bottom-start'
): MenuHandler => {
  return popMenu(target, {
    options: {
      items: options,
      onClose,
    },
    placement,
  });
};
